// Editing a field value: inline inputs for text-like fields, pick lists for terms, records and users. One editor for table, board and record.

import { useRef, useState } from 'react';
import { FIELD_TYPES, type FieldDef, type RecordObj } from '@confdeck/core-shared';
import { setField } from '../sync/mutations.ts';
import { useLinked, useSync } from '../sync/react.tsx';
import { BlockEditor } from '../editor/BlockEditor.tsx';
import { NOTE_KINDS } from '../editor/kinds.ts';
import { Input } from '../ui/primitives.tsx';
import { DateInput } from '../ui/DateInput.tsx';
import { normalizePhone, parseMoney } from './format.ts';
import { RecordPicker, TermPicker, UserPicker } from './pickers.tsx';
import { QueryEditor } from './QueryField.tsx';
import s from './fields.module.css';

/** Fields edited through a pick list or a large text box open in a popover; the rest edit in place. */
export const POPOVER_FIELDS = new Set(['taxonomy', 'relation', 'user', 'longtext', 'query']);
/** Editors that bring their own width (the block editor, the filter builder of a query). */
export const WIDE_FIELDS = new Set(['longtext', 'query']);

function toInput(field: FieldDef, v: unknown): string {
  if (v == null) return '';
  if (field.fieldType === 'money' && typeof v === 'number') return String(v / 100);
  return String(v);
}

/** Converts typed text to a stored value; returns an error message instead when it does not parse. */
function fromInput(field: FieldDef, text: string): { value: unknown } | { error: string } {
  const t = text.trim();
  if (!t) return { value: null };
  let v: unknown = t;
  if (field.fieldType === 'number') { v = Number(t.replace(/\s/g, '').replace(',', '.')); if (Number.isNaN(v)) return { error: 'Нужно число' }; }
  if (field.fieldType === 'money') { v = parseMoney(t); if (Number.isNaN(v as number)) return { error: 'Сумма, например 150 000 или 1,5 млн' }; }
  if (field.fieldType === 'phone') v = normalizePhone(t);
  if (field.fieldType === 'url' && !/^https?:\/\//.test(t)) v = `https://${t}`;
  const err = FIELD_TYPES[field.fieldType].validate(v, field);
  return err ? { error: err } : { value: v };
}

export function InlineEditor({ rec, field, onDone, initial, className }: { rec: RecordObj; field: FieldDef; onDone: (move?: 'down' | 'right' | 'left') => void; initial?: string; className?: string }) {
  const client = useSync();
  const [text, setText] = useState(initial ?? toInput(field, rec.data[field.key]));
  const [error, setError] = useState<string | null>(null);
  const commit = (move?: 'down' | 'right' | 'left') => {
    const r = fromInput(field, text);
    if ('error' in r) { setError(r.error); return; }
    if (field.required && r.value == null) { setError('Обязательное поле'); return; }
    if (r.value !== (rec.data[field.key] ?? null)) setField(client, rec, field.key, r.value);
    onDone(move);
  };
  const keys = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); commit('down'); }
    else if (e.key === 'Tab') { e.preventDefault(); commit(e.shiftKey ? 'left' : 'right'); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onDone(); }
  };
  return (
    <div className={s.editorBox}>
      {field.fieldType === 'date'
        ? <DateInput autoFocus bare className={className} value={text} aria-invalid={!!error} onChange={(v) => { setText(v); setError(null); }} onKeyDown={keys} onBlur={() => commit()} />
        : <Input autoFocus bare className={className} type={field.fieldType === 'email' ? 'email' : 'text'} value={text} aria-invalid={!!error}
          inputMode={field.fieldType === 'number' || field.fieldType === 'money' ? 'decimal' : undefined}
          onChange={(e) => { setText(e.target.value); setError(null); }} onKeyDown={keys} onBlur={() => commit()} />}
      {error && <div role="alert" className={s.editorError}>{error}</div>}
    </div>
  );
}

export function PopoverEditor({ rec, field, onDone, pipelineId }: { rec: RecordObj; field: FieldDef; onDone: () => void; pipelineId?: string }) {
  const client = useSync();
  const linked = useLinked(rec, field.key);
  const v = rec.data[field.key];
  if (field.fieldType === 'taxonomy') return <TermPicker field={field} value={v} pipelineId={pipelineId} onDone={onDone} onChange={(nv) => setField(client, rec, field.key, nv)} />;
  if (field.fieldType === 'relation') return <RecordPicker field={field} value={linked} onDone={onDone} onChange={(ids) => setField(client, rec, field.key, ids)} />;
  if (field.fieldType === 'user') return <UserPicker value={v} onDone={onDone} {...(field.options.multiple ? { onChangeMany: (nv: string[]) => setField(client, rec, field.key, nv) } : { onChange: (nv: string | null) => setField(client, rec, field.key, nv) })} />;
  if (field.fieldType === 'longtext') return <LongTextEditor rec={rec} field={field} onDone={onDone} />;
  if (field.fieldType === 'query') return <QueryEditor rec={rec} field={field} onDone={onDone} />;
  return null;
}

/** A long text of a cell, written in the core's block editor as on the card (decision 034); leaving it saves, Ctrl+Enter too. */
function LongTextEditor({ rec, field, onDone }: { rec: RecordObj; field: FieldDef; onDone: () => void }) {
  const client = useSync();
  // The editor gets the text as typed: an old `value` would look like someone else's edit when focus leaves for the block menu,
  // and the editor would take it back (MiniMax review P1).
  const [md, setMd] = useState(String(rec.data[field.key] ?? ''));
  const text = useRef(md);
  const save = () => { const v = text.current.trim() ? text.current : null; if (v !== (rec.data[field.key] ?? null)) setField(client, rec, field.key, v); onDone(); };
  return (
    <div className={s.longBox}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null) && !(e.relatedTarget as HTMLElement | null)?.closest('[role="listbox"],[role="dialog"]')) save(); }}
      onKeyDownCapture={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); e.stopPropagation(); save(); } }}>
      <BlockEditor compact autoFocus value={md} label={field.name} kinds={NOTE_KINDS} onChange={(x) => { text.current = x; setMd(x); }} />
    </div>
  );
}
